import { isDefined } from "@clipboard-health/util-ts";
import { Box, type SxProps, type Theme } from "@mui/material";
import { type SystemStyleObject } from "@mui/system";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { HeaderMenuButton } from "@src/appV2/redesign/components/HeaderMenuButton/HeaderMenuButton";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { MaterialUiProviders } from "@src/mui/providers/MaterialUiProviders";
import React from "react";

export interface LegacyRouteWrapperProps {
  children: React.ReactNode;
  rootSx?: SystemStyleObject<Theme>;
  /**
   * Whether to render the fallback hamburger row above the content. Defaults to
   * `true`. Pages that render their own inline `HeaderMenuButton` (e.g. Berlin
   * redesign pages with a clean title) pass `false` to avoid a double button.
   */
  renderHeaderMenuButton?: boolean;
}

export function LegacyRouteWrapper(props: LegacyRouteWrapperProps) {
  const { children, rootSx, renderHeaderMenuButton = true } = props;
  // Legacy pages render in the legacy MUI theme (inside MaterialUiProviders) and
  // have no shared header to host the inline hamburger. Render it once here, above
  // the scroll area and re-wrapped in the Berlin theme so it styles correctly. The
  // row only mounts on mobile, so desktop is unaffected.
  const showHeaderMenuButton = useIsMobile() && renderHeaderMenuButton;

  const rootContainerSx: SxProps<Theme> = [
    { display: "flex", flexDirection: "column", flex: 1 },
    ...(isDefined(rootSx) ? [rootSx] : []),
  ];

  return (
    <MaterialUiProviders>
      <Box sx={rootContainerSx}>
        {showHeaderMenuButton && (
          <BerlinThemeProvider>
            <HeaderMenuButton sx={{ marginTop: 3, marginX: 3 }} />
          </BerlinThemeProvider>
        )}
        <Box
          sx={(theme) => ({
            padding: 3,
            flex: 1,
            minHeight: 0,
            // On mobile, the parent PageWrapperMobile sets overflow:hidden on direct
            // children to support touch gestures/bottom sheets. This inner Box is not
            // targeted by that selector, so it can create its own scroll context.
            [theme.breakpoints.down("md")]: {
              overflow: "auto",
            },
            // On mobile/tablet, add padding to account for fixed floating navigation
            [theme.breakpoints.down("lg")]: {
              ...(theme.size?.floatingNavigation && {
                paddingBottom: `calc(${theme.size.floatingNavigation.height} + ${
                  theme.size.floatingNavigation.margin
                } + ${theme.spacing(3)})`,
              }),
            },
          })}
        >
          {children}
        </Box>
      </Box>
    </MaterialUiProviders>
  );
}
